import { create } from 'zustand';

export enum LoadingStatus {
    IDLE = "idle",
    LOADING = "loading",
    LOADED = "loaded",
    ERROR = "error"
}

export interface LoadingItem {
    url: string;
    status: LoadingStatus;
    error?: string;
}

interface LoadingStore {
    items: Map<string, LoadingItem>;
    getItem: (url: string) => LoadingItem | undefined;
    setStatus: (url: string, status: LoadingStatus, error?: string) => void;
    reset: () => void;
    onLoad: (resources?: string[]) => Promise<void>;
}

// Pure store - no side effects, no external integrations
export const useLoadingStore = create<LoadingStore>((set, get) => ({
    items: new Map(),
    getItem: (url) => get().items.get(url),
    setStatus: (url, status, error) => set(state => {
        const key = Array.isArray(url) ? url[0] : url;
        const items = new Map(state.items);
        items.set(key, {
            url: key,
            status,
            error,
        });
        return { items };
    }),
    reset: () => set({ items: new Map() }),
    onLoad: (resources) =>
        new Promise((resolve) => {
            const isLoaded = () => {
                const items = get().items;
                const keys = resources && resources.length
                    ? resources
                    : Array.from(items.keys());

                if (!keys.length) return false;
                return keys.every((k) => items.get(k)?.status === LoadingStatus.LOADED);
            };

            if (isLoaded()) return resolve();

            const unsub = useLoadingStore.subscribe(() => {
                if (isLoaded()) {
                    unsub();
                    resolve();
                }
            });
        }),
}));
